// =============================================================================
// Add to cart.
//
// One delegated listener covers every purchase button in the theme — the icon
// button on each product card, and the two text buttons on a product page —
// because they all carry `data-add-id` and differ only in whether a quantity
// control is within reach.
//
// Delegation rather than per-button binding: the site editor replaces
// ServerSideRender output wholesale whenever an inspector control changes, and
// listeners bound to the discarded nodes would go with them.
//
// The parent theme's own add-to-cart handler keys on `.az-add-to-cart` and on
// `a[href*="add-to-cart="]`. These buttons carry neither, so the two never both
// fire on one click.
// =============================================================================

import { addToCart } from "@parent/js/frontend/modules/cart-api.js";

import { str, url } from "./config.js";
import { syncCartCount } from "./cart-count.js";
import { readQty } from "./qty.js";
import { showToast } from "./toast.js";

/** How long a card's button stays blue after a successful add. */
const ADDED_MS = 900;

/**
 * How long the "Heading to checkout" toast gets before the page changes.
 *
 * Without it the message is never read — navigation replaces the document the
 * toast is painted into.
 */
const CHECKOUT_DELAY_MS = 600;

export default function initAddToCart() {
	document.addEventListener("click", onClick);
}

/** @param {MouseEvent} event */
async function onClick(event) {
	const button = event.target.closest("[data-add-id]");

	if (!button) {
		return;
	}

	// These are all `type="button"`, so there is no form submission to suppress.
	// It is still called: a card's button sits inside an anchor, and the price
	// of being wrong about that is a navigated-away click.
	event.preventDefault();

	const productId = Number(button.dataset.addId);

	if (!Number.isFinite(productId) || productId <= 0) {
		return;
	}

	const title = button.dataset.addTitle || "";
	const platform = button.dataset.addPlatform || "";
	const isPreorder = button.dataset.addPreorder === "true";
	const buyNow = button.hasAttribute("data-buy-now");

	// Only a product page has a quantity control, marked up as the scope the
	// stepper and the buttons share. Everywhere else `readQty` falls back to one.
	const scope = button.closest("[data-add-scope]");
	const quantity = readQty(scope);

	// A text button can say what is happening; the card's button is icon-only and
	// writing a label into it would throw the SVG away with nothing to restore.
	const label = button.querySelector("svg") ? null : button.textContent;

	button.disabled = true;

	if (label !== null) {
		button.textContent = str("adding", "Adding…");
	}

	let cart;

	try {
		cart = await addToCart(productId, quantity);
	} catch {
		button.disabled = false;

		if (label !== null) {
			button.textContent = label;
		}

		showToast(str("addFailed", "That didn't go into the cart. Please try again."));
		return;
	}

	syncCartCount(cart);

	// The mini-cart drawer's body is server-rendered, so the Store API path
	// cannot update it directly the way cart-count.js updates the badge. This
	// announces the change; the drawer marks itself stale and re-pulls the next
	// time it is opened (see modules/minicart.js).
	document.dispatchEvent(new CustomEvent("aug:cart-updated", { detail: { cart } }));

	if (buyNow) {
		showToast(str("toCheckout", "Heading to checkout"), `${title} × ${quantity}`);

		// The button is left disabled on purpose: the document is about to be
		// replaced, and re-enabling it would invite a second order in the gap.
		window.setTimeout(() => {
			window.location.assign(url("checkoutUrl", "/checkout"));
		}, CHECKOUT_DELAY_MS);

		return;
	}

	button.disabled = false;

	if (label !== null) {
		button.textContent = label;
	}

	// Styled on `.add-btn`, which is the card's button. The product page's text
	// buttons get no flash — the toast is their confirmation.
	button.classList.add("is-added");
	window.setTimeout(() => button.classList.remove("is-added"), ADDED_MS);

	showToast(
		isPreorder ? str("preordered", "Pre-order confirmed") : str("added", "Added to cart"),
		// The two surfaces word this differently in the design: a card names the
		// platform, a product page names the quantity just chosen.
		scope ? `${title} × ${quantity}` : [title, platform].filter(Boolean).join(" — ")
	);
}
